import React, { useState, CSSProperties, useEffect } from 'react'
import useAdminSearch, { SearchType } from './hooks/useAdminSearch'
import { HighlightedText, Flex, Avatar } from '@ui'
import cx from 'classnames'
import AdminSearchDropdown, {
  AdminSearchItemType,
  IAdminSearchResultItem,
  IAdminSearchResultItems,
} from './AdminSearchDropdown'
import { IEmployee } from '@api'
import { Icon, Link } from '@ui'
import { Menu, MenuItem } from '@blueprintjs/core'
import {
  IAdminSearchEmployee,
  IAdminSearchDepartment,
  IAdminSearchNeighborhood,
  IAdminSearchSpace,
  ISeat,
} from '@api/types'
import { debounce, get, isEmpty, isEqual, omit } from 'lodash'
import CodeFork from './assets/CodeFork'
import { useFloor, useMapInit, useAdminMapParams } from '@providers/mapProvider'
import css from './employeeManagement.module.scss'
import useSettings from 'components/NewSettings/hooks/useSettings'
import { Tag } from '@runeffective/evo-design-system'
import { Icon as EvoIcon } from '@runeffective/evo-design-system'
import getSearchFilterDetails from '@api/adminSearch/searchFilterDetails'
import { isBookingManager, isIT, isViewer } from '@utils'
import { useUser } from '@hooks'
import { Size } from '@ui/Avatar'
import { Tooltip2 } from '@blueprintjs/popover2'
import api from '@api'

type IAdminSearchType<T> = T & { type: AdminSearchItemType }

function isEmployee(
  value: IAdminSearchResultItem | null
): value is IAdminSearchType<IAdminSearchEmployee> {
  if (value === null) return false

  return (value as IAdminSearchResultItem).type === AdminSearchItemType.EMPLOYEE
}

function isNeighborhood(
  value: IAdminSearchResultItem | null
): value is IAdminSearchType<IAdminSearchNeighborhood> {
  if (value === null) return false

  return (value as IAdminSearchResultItem).type === AdminSearchItemType.NEIGHBORHOOD
}

function isDepartment(
  value: IAdminSearchResultItem | null
): value is IAdminSearchType<IAdminSearchDepartment> {
  if (value === null) return false

  return (value as IAdminSearchResultItem).type === AdminSearchItemType.DEPARTMENT
}

function isSpace(
  value: IAdminSearchResultItem | null
): value is IAdminSearchType<IAdminSearchSpace> {
  if (value === null) return false

  return (value as IAdminSearchResultItem).type === AdminSearchItemType.SPACE
}

enum Icons {
  ALL = 'light fa-list-ul',
  Employees = 'people-simple',
  Departments = 'custom-office-light',
  Spaces = 'custom-office-space-light',
  Neighborhoods = 'custom-open-collab-space-light',
  TRIANGLE = 'sharp fa-triangle fa-solid',
}

type FilterCountType = {
  totalEmployes: string
  totalDepartments: string
  totalSpaces: string
  totalNeighborhoods: string
}

export default function AdminSearch({
  navigate,
  setShowFilters,
  setShowViewFilters,
  showFilters,
  setSplitScreen,
  setUnseatedDrawer,
}: Readonly<{
  navigate?: (to: string) => void
  setShowFilters: (showFilters: boolean) => void
  setShowViewFilters: (showViewFilters: boolean) => void
  showFilters: boolean
  setSplitScreen: (value: boolean) => void
  setUnseatedDrawer: (value: boolean) => void
}>) {
  const floor = useFloor()
  const settings = useSettings()
  const { apId, floorId, setFloorId } = floor
  const [spaceId, setSpaceId] = useState('')
  const [searchType, setSearchType] = useState(SearchType.ALL)

  const map = useMapInit()
  const adminSearch = useAdminSearch({ apId, searchType })
  const { setParams } = useAdminMapParams()
  const onSelect = (employee: IEmployee) => {
    navigate && employee && navigate(`?employeeId=${employee.id}`)
  }

  useEffect(() => {
    if (!isEmpty(floor?.selection?.items))
      setParams?.({
        selectedNeighborhoodId: null,
        selectedDepartmentId: null,
      })
  }, [floor?.selection?.items, setParams])

  useEffect(() => {
    if (map.current) {
      zoomToSpace()
    }
  }, [floor.map, spaceId])

  const zoomToSpace = async () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    map?.current?.zoomTo(await api.space.getSpaceCoordinates(spaceId))
  }

  const onSelectItem = async (val: IAdminSearchResultItem | null) => {
    floor?.isBookDeskPage && floor?.setIsBookDeskPage?.(floorId)
    floor?.isBookMeetingRoomPage && floor?.setIsBookMeetingRoomPage?.(floorId)
    if (map.current) {
      if (isEmployee(val)) {
        navigate?.(`?employeeId=${val.id}`)

        if (val.seat) {
          floor.selection?.add([val.seat.id])
        }
      }

      if (isNeighborhood(val)) {
        const isAnotherFloor = floorId !== val.floor.id

        if (isAnotherFloor) {
          setFloorId?.(val.floor.id)
        }
        //zooming to neighborhood spaces is done in Map.txs
        setParams?.((params) => ({ ...params, selectedNeighborhoodId: val.id }))
      }

      if (isDepartment(val)) {
        navigate?.(`?departmentId=${val.id}&isAdminSearch=${true}`)
      }

      if (isSpace(val)) {
        const isAnotherFloor = floorId !== val.floor.id

        setSpaceId(val.id)
        if (isAnotherFloor) {
          floor.selection?.add([val.id])
          setFloorId?.(val.floor.id)
        } else {
          floor.selection?.add([val.id])
          zoomToSpace()
        }
      }
    }
  }

  const toggleSearchType = (type: SearchType) => {
    setSearchType(type)
    setShowFilters(false)
  }

  const closeRightDrawers = () => {
    floor?.selection?.clear()
    setUnseatedDrawer(false)
    setShowViewFilters(false)
  }

  const [filterMenuCount, setFilterMenuCount] = useState({} as FilterCountType)
  async function fetchFilterData() {
    const filterDetails = await getSearchFilterDetails()
    const filterCount: FilterCountType = {
      totalEmployes: get(filterDetails, 'employeeConnection.counters.total', '0'),
      totalDepartments: get(filterDetails, 'departmentConnection.counters.total', '0'),
      totalSpaces: get(filterDetails, 'spaceConnection.counters.total', '0'),
      totalNeighborhoods: get(filterDetails, 'neighborhoodConnection.counters.total', '0'),
    }
    setFilterMenuCount(filterCount)
  }

  useEffect(() => {
    fetchFilterData()
  }, [])

  const getIcon = () => {
    switch (searchType) {
      case SearchType.ALL:
        return Icons.ALL
      case SearchType.DEPARTMENTS:
        return Icons.Departments
      case SearchType.EMPLOYEES:
        return Icons.Employees
      case SearchType.SPACES:
        return Icons.Spaces
      case SearchType.NEIGHBORHOODS:
        return SearchType.NEIGHBORHOODS
    }
  }
  return (
    <Flex style={{ position: 'relative' }}>
      <Flex
        justify="center"
        align="center"
        className={`${showFilters && css.adminFilterSelected} ${css.adminFilter}`}
        data-button="admin_search"
        onClick={() => {
          setShowFilters(!showFilters)
          setSplitScreen(false)
          setUnseatedDrawer(false)
          setShowViewFilters(false)
        }}>
        <EvoIcon iconName={getIcon()} iconStyle="fal" size="sm" />
      </Flex>
      {showFilters && (
        <Filters
          toggleSearchType={toggleSearchType}
          searchType={searchType}
          filterCount={filterMenuCount}
        />
      )}
      <AdminSearchDropdown
        {...{
          'data-input': 'admin_search',
          search: adminSearch,
          onSelect,
          width: '310px',
          test: 'admin_search',
        }}
        leftIcon={<Icon name="search" />}
        rightIcon={<Icon name="close" />}
        onSelect={(val) => onSelectItem(val)}
        renderItem={(item, helpers) => {
          const onSeatClick = async (seat: ISeat) => {
            floor?.isBookDeskPage && floor?.setIsBookDeskPage?.(floorId)
            floor?.isBookMeetingRoomPage && floor?.setIsBookMeetingRoomPage?.(floorId)

            const isAnotherFloor = floorId !== seat.floor.id

            setSpaceId(seat.id)
            if (isAnotherFloor) {
              setFloorId?.(seat.floor.id)
            } else {
              zoomToSpace()
            }
            if (map.current) {
              /* eslint-disable @typescript-eslint/ban-ts-comment */
              // @ts-ignore
              map.current.actions.select(seat.id, true)
              helpers?.closeMenu?.()
            }
          }

          switch (item.type) {
            case AdminSearchItemType.EMPLOYEE:
              return (
                <EmployeeItem
                  key={item.id}
                  item={omit(item, 'type') as IAdminSearchEmployee}
                  query={adminSearch.query}
                  onSeatClick={(seat) => onSeatClick(seat)}
                  viewPeerCurrentBookings={settings.viewPeerCurrentBookings}
                  onSelectItem={onSelectItem}
                  closeMenu={helpers?.closeMenu}
                />
              )

            case AdminSearchItemType.DEPARTMENT:
              return (
                <DepartmentItem
                  key={item.id}
                  item={omit(item, 'type') as IAdminSearchDepartment}
                  query={adminSearch.query}
                />
              )

            case AdminSearchItemType.NEIGHBORHOOD:
              return (
                <NeighborhoodItem
                  key={item.id}
                  item={omit(item, 'type') as IAdminSearchNeighborhood}
                  query={adminSearch.query}
                />
              )

            case AdminSearchItemType.SPACE:
              return (
                <SpaceItem
                  key={item.id}
                  item={omit(item, 'type') as IAdminSearchSpace}
                  query={adminSearch.query}
                />
              )

            default:
              return null
          }
        }}
        setShowFilters={setShowFilters}
        closeRightDrawers={closeRightDrawers}
      />
    </Flex>
  )
}

const EmployeeItem = ({
  item,
  query,
  onSeatClick,
  viewPeerCurrentBookings,
  onSelectItem,
  closeMenu,
}: {
  item: IAdminSearchEmployee
  query: string
  onSeatClick: (seat: ISeat) => void
  viewPeerCurrentBookings?: boolean
  onSelectItem: (val: IAdminSearchResultItem | null) => void
  closeMenu: any
}) => {
  const primarySeat = item.seat
  const secondarySeats = item.secondarySeats?.edges.map((s) => s.node) || []
  const seats = [...(primarySeat ? [primarySeat] : []), ...secondarySeats]

  return (
    <Flex align="stretch" direction="column" className="flex-auto">
      <div>
        <Flex align="flex-start" style={{ height: '28px', lineHeight: '28px' }}>
          <Avatar assets={[{ url: item.assets.url || '' }]} name={item.name} size={Size.XS} />
          <Flex direction="column" className="flex-auto">
            <Flex className="flex-auto ml-18px" justify="space-between" align="center">
              <HighlightedText test="employee_search_item" text={item.name} query={query} />
              {!item.seat && <Tag title={'Unseated'} variant="Warning"></Tag>}
              {viewPeerCurrentBookings && (item.bookings?.length || 0) > 0 && (
                <div
                  style={{ fontSize: 12 }}
                  onClick={(ev) => {
                    ev.stopPropagation()
                    alert('Needs to be fixed')
                    // showBookingsModal()
                    // employeeBooking({
                    //   name: item.name,
                    //   bookings: item.bookings,
                    //   locateDesk: item.locateDesk,
                    // })
                  }}>
                  <a style={{ color: 'var(--primary-color)' }}>Bookings</a>
                  <div className={css.bookingCount}>{item.bookings?.length}</div>
                </div>
              )}
            </Flex>
          </Flex>
        </Flex>
      </div>
      <Flex direction="column" style={{ marginLeft: 48 }}>
        {(item.team !== null || item.department !== null) && (
          <span className={cx(css.employee_management_gray_text, 'noto')}>
            {item?.team !== null
              ? `${item?.department?.name} | ${item?.team?.name}`
              : item.department.name}
          </span>
        )}
        {Boolean(seats?.length) && (
          <Flex wrap="wrap" align="center" style={{ marginTop: '4px' }}>
            <Icon name="chair" style={{ color: '#404861', fontWeight: 300 }} />
            {seats.map((s, index, arr) => (
              <Flex
                key={s.id}
                align="center"
                style={{
                  lineHeight: '20px',
                }}>
                <span
                  className={css.space_link}
                  onClick={(e: React.SyntheticEvent<EventTarget>) => {
                    e.stopPropagation()
                    onSeatClick(s)
                  }}>
                  <Tooltip2 content={'Locate Seat'} position="right" key={s.id}>
                    {s.external_id}
                  </Tooltip2>
                </span>
                {index === arr?.length - 1 ? null : <span>,</span>}
              </Flex>
            ))}
          </Flex>
        )}
        {Boolean(item.neighborhoods?.length) && (
          <Flex wrap="wrap" align="center" style={{ marginTop: '4px' }}>
            <span>Neighborhoods</span>
            {item.neighborhoods.map((n, index, arr) => (
              <Flex
                key={n.id}
                align="center"
                style={{
                  lineHeight: '20px',
                }}>
                <span
                  className={css.space_link}
                  onClick={(e: React.SyntheticEvent<EventTarget>) => {
                    e.stopPropagation()
                    closeMenu()
                    onSelectItem({ ...n, type: AdminSearchItemType.NEIGHBORHOOD })
                  }}>
                  <Tooltip2 content={'Locate Neighborhood'} position="right" key={n.id}>
                    {n.name}
                  </Tooltip2>
                </span>
                {index === arr?.length - 1 ? null : <span>,</span>}
              </Flex>

              // </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}

const DepartmentItem = ({ item, query }: { item: IAdminSearchDepartment; query: string }) => (
  <Flex align="stretch" className="flex-auto" direction="column">
    <Flex align="center" style={{ height: '28px', lineHeight: '28px' }}>
      <CodeFork />
      <Flex className="flex-auto ml-18px" justify="space-between">
        <HighlightedText test="department_search_item" text={item.name} query={query} />
      </Flex>
    </Flex>
  </Flex>
)

const NeighborhoodItem = ({ item, query }: { item: IAdminSearchNeighborhood; query: string }) => (
  <Flex align="stretch" direction="column" className="flex-auto">
    <Flex align="center" style={{ height: '28px', lineHeight: '28px' }}>
      <Icon name="users" />
      <Flex direction="column">
        <Flex className="flex-auto ml-18px" justify="space-between">
          <HighlightedText test="neighborhood_search_item" text={item.name} query={query} />
        </Flex>
      </Flex>
    </Flex>
    <Flex className="flex-auto ml-18px" justify="space-between">
      <span className={css.space_link} style={{ marginLeft: 13 }}>
        <Tooltip2 content={'Locate Neighborhood'} position="right" key={item.floor.id}>
          {`${item.building.name} ${item.floor.name}`}
        </Tooltip2>
      </span>
    </Flex>
  </Flex>
)

const SpaceItem = ({ item, query }: { item: IAdminSearchSpace; query: string }) => (
  <Flex align="stretch" direction="column" className="flex-auto">
    <Flex align="center" style={{ height: '28px', lineHeight: '28px' }}>
      <Icon name="chair" />
      <Flex className="flex-auto ml-18px" justify="space-between">
        <HighlightedText test="space_search_item" text={item.externalId} query={query} />
      </Flex>
    </Flex>
  </Flex>
)

function Filters({
  toggleSearchType,
  searchType,
  filterCount,
}: {
  toggleSearchType: (type: SearchType) => void
  searchType: string
  filterCount: FilterCountType
}) {
  const { user } = useUser()
  return (
    <div className="wo-padding as-filter-container">
      <EvoIcon className={css.triangleIcon} iconName={Icons.TRIANGLE} iconStyle="fal" size="sm" />
      <Menu>
        <MenuItem
          text={<FilterMenuItem itemName="All" iconName={Icons.ALL} searchType={searchType} />}
          onClick={() => toggleSearchType(SearchType.ALL)}
        />
        <MenuItem
          text={
            <FilterMenuItem
              itemName="Employees"
              iconName={Icons.Employees}
              searchType={searchType}
              filterMenuCount={filterCount.totalEmployes}
            />
          }
          onClick={() => toggleSearchType(SearchType.EMPLOYEES)}
        />

        {!(isBookingManager(user?.role) || isViewer(user?.role) || isIT(user?.role)) && (
          <MenuItem
            text={
              <FilterMenuItem
                itemName="Departments"
                iconName={Icons.Departments}
                searchType={searchType}
                filterMenuCount={filterCount.totalDepartments}
              />
            }
            onClick={() => toggleSearchType(SearchType.DEPARTMENTS)}
          />
        )}
        <MenuItem
          text={
            <FilterMenuItem
              itemName="Spaces"
              iconName={Icons.Spaces}
              searchType={searchType}
              filterMenuCount={filterCount.totalSpaces}
            />
          }
          onClick={() => toggleSearchType(SearchType.SPACES)}
        />

        {!(isBookingManager(user?.role) || isViewer(user?.role) || isIT(user?.role)) && (
          <MenuItem
            text={
              <FilterMenuItem
                itemName="Neighborhoods"
                iconName={Icons.Neighborhoods}
                searchType={searchType}
                filterMenuCount={filterCount.totalNeighborhoods}
              />
            }
            onClick={() => toggleSearchType(SearchType.NEIGHBORHOODS)}
          />
        )}
      </Menu>
    </div>
  )
}

export const FilterMenuItem = ({
  itemName,
  iconName,
  searchType,
  filterMenuCount,
}: {
  itemName: string
  iconName: string
  searchType: string
  filterMenuCount?: string
}) => {
  const isMenuSelected = () => isEqual(searchType.toLocaleLowerCase(), itemName.toLocaleLowerCase())

  return (
    <div className={`${css.filterMenuItem} ${isMenuSelected() && css.selectedMenu}`}>
      <span className={css.itemIconWrapper}>
        <EvoIcon iconName={iconName} iconStyle="fal" size="sm" />
      </span>
      <span className={css.itemName}>
        {itemName}
        {filterMenuCount && <Tag title={filterMenuCount} variant="Informational"></Tag>}
      </span>
    </div>
  )
}

export function MenuSeparator({
  text,
  paddingRight,
  containerStyle,
}: {
  text: string
  paddingRight?: number | string
  containerStyle?: CSSProperties
}) {
  return (
    <div style={containerStyle} className="bp4-menu-item flex relative inactive pl-0">
      <span className="as-separator" style={{ paddingRight }}>
        {text}
      </span>
      <div className="hline" />
    </div>
  )
}
